import React, { useState } from 'react';
import './About.css';

const TABS = [
  { id: 'why', label: 'Why CommsCoach' },
  { id: 'architecture', label: 'Architecture' },
  { id: 'design', label: 'Design Principles' },
  { id: 'bau-flow', label: 'BAU Flow' },
  { id: 'impact', label: 'Impact' },
];

export default function About() {
  const [activeTab, setActiveTab] = useState('why');

  return (
    <div className="about-page">
      {/* Tab Navigation */}
      <div className="about-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`about-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="about-content">
        {activeTab === 'why' && <WhyTab />}
        {activeTab === 'architecture' && <ArchitectureTab />}
        {activeTab === 'design' && <DesignPrinciplesTab />}
        {activeTab === 'bau-flow' && <BauFlowTab />}
        {activeTab === 'impact' && <ImpactTab />}
      </div>
    </div>
  );
}

/* ─── Why CommsCoach Tab ─── */
function WhyTab() {
  return (
    <div className="tab-section">
      <h2 className="page-title">Why CommsCoach?</h2>

      {/* Hero Image */}
      <div className="why-hero-image">
        <img src={`${process.env.PUBLIC_URL}/why-comms-coach.png`} alt="Why CommsCoach — Problem & Solution" />
      </div>
    </div>
  );
}

/* ─── Architecture Tab ─── */
function ArchitectureTab() {
  return (
    <div className="tab-section">
      <h2 className="page-title">System Architecture</h2>
      <p className="section-desc">
        Microservice architecture running on the <strong>ARCAM Kubernetes cluster</strong> —
        independently scalable services, optimized LLM inference with warmed GPU models,
        WebSocket real-time chat, and fully GitOps-managed deployment.
      </p>

      {/* Architecture Diagram (SVG) */}
      <div className="arch-diagram-wrapper">
        <img src={`${process.env.PUBLIC_URL}/architecture-diagram.svg`} alt="CommsCoach Architecture Diagram" className="arch-diagram-img" />
      </div>
    </div>
  );
}

/* ─── Design Principles Tab ─── */
function DesignPrinciplesTab() {
  return (
    <div className="tab-section">
      <h2 className="page-title">Design Principles</h2>
      <p className="section-desc">
        Core engineering decisions that make CommsCoach performant, secure, and maintainable.
        Built with <strong>Kiro</strong> for AI-assisted development, requirements gathering, and solution design.
        LLM inference powered by <strong>Ollama</strong> for self-hosted model management.
      </p>

      <div className="principles-grid">
        <div className="principle-card">
          <div className="principle-icon">🏗️</div>
          <h4>Infrastructure as Code</h4>
          <p>Terraform manages all AWS resources — DocumentDB, ElastiCache, ECR, IAM roles, security groups. Versioned and repeatable.</p>
        </div>
        <div className="principle-card">
          <div className="principle-icon">🔀</div>
          <h4>Microservice Isolation</h4>
          <p>Each module independently deployable on Kubernetes. Changes to core-backend don't rebuild exam-backend. Per-service Helm charts.</p>
        </div>
        <div className="principle-card">
          <div className="principle-icon">🔐</div>
          <h4>Zero Hardcoded Secrets</h4>
          <p>All secrets via AWS Secrets Manager + IRSA pod-level policies. Environment variables standardized with <code>CCO_</code> prefix.</p>
        </div>
        <div className="principle-card">
          <div className="principle-icon">⚡</div>
          <h4>Optimized Latency</h4>
          <p>Model warmup on startup + 23h periodic re-warm keeps LLM loaded on GPU. Zero cold starts — consistent ~4s response time.</p>
        </div>
        <div className="principle-card">
          <div className="principle-icon">📡</div>
          <h4>WebSocket Real-time</h4>
          <p>Assessment sessions use persistent WebSocket connections for low-latency bi-directional chat delivery with disconnect recovery.</p>
        </div>
        <div className="principle-card">
          <div className="principle-icon">🗄️</div>
          <h4>Shared Data, Owned Collections</h4>
          <p>Single DocumentDB cluster — each service owns its collections, reads others, never cross-writes. Data consistency without coupling.</p>
        </div>
        <div className="principle-card">
          <div className="principle-icon">☸️</div>
          <h4>GitOps Delivery</h4>
          <p>GitLab CI → Docker build → ECR push → Helm charts → ArgoCD sync. Automatic rollouts on merge to stage/main branches.</p>
        </div>
        <div className="principle-card">
          <div className="principle-icon">🛡️</div>
          <h4>Secure by Design</h4>
          <p>Post-deploy RADAR security scans in GitLab CI pipeline. MSAL PKCE (no client secrets), TLS-only DocumentDB, IRSA least-privilege access.</p>
        </div>
      </div>

      {/* Toolchain */}
      <h3>Toolchain</h3>
      <div className="toolchain-grid">
        <div className="tool-item">
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/gitlab/gitlab-original.svg" alt="GitLab" className="tool-icon" />
          <span>GitLab CI/CD</span>
        </div>
        <div className="tool-item">
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg" alt="Terraform" className="tool-icon" />
          <span>Terraform</span>
        </div>
        <div className="tool-item">
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg" alt="Kubernetes" className="tool-icon" />
          <span>Kubernetes</span>
        </div>
        <div className="tool-item">
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/argocd/argocd-original.svg" alt="ArgoCD" className="tool-icon" />
          <span>ArgoCD</span>
        </div>
        <div className="tool-item">
          <img src="https://helm.sh/img/helm.svg" alt="Helm" className="tool-icon" />
          <span>Helm</span>
        </div>
        <div className="tool-item">
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg" alt="Docker" className="tool-icon" />
          <span>Docker</span>
        </div>
        <div className="tool-item">
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg" alt="AWS" className="tool-icon" />
          <span>AWS</span>
        </div>
        <div className="tool-item">
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg" alt="Python" className="tool-icon" />
          <span>FastAPI</span>
        </div>
        <div className="tool-item">
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" alt="React" className="tool-icon" />
          <span>React</span>
        </div>
        <div className="tool-item">
          <img src="https://ollama.com/public/icon-32x32.png" alt="Ollama" className="tool-icon" />
          <span>Ollama</span>
        </div>
        <div className="tool-item">
          <img src={`${process.env.PUBLIC_URL}/kiro-logo.svg`} alt="Kiro" className="tool-icon" />
          <span>Kiro</span>
        </div>
      </div>
    </div>
  );
}

/* ─── BAU Flow Tab ─── */
function BauFlowTab() {
  return (
    <div className="tab-section">
      <h2 className="page-title">BAU Flow</h2>
      <p className="section-desc">
        End-to-end operational flows for both Admin and Candidate journeys.
      </p>

      {/* Admin Flow */}
      <h3>Admin Journey</h3>
      <div className="flow-visual flow-visual--horizontal">
        <div className="flow-stage">
          <div className="flow-stage-num">1</div>
          <div className="flow-stage-title">SSO Login</div>
          <div className="flow-stage-desc">Admin authenticates via Azure AD MSAL PKCE. Backend validates and assigns role (admin/superadmin).</div>
        </div>
        <div className="flow-stage">
          <div className="flow-stage-num">2</div>
          <div className="flow-stage-title">Create Candidates</div>
          <div className="flow-stage-desc">Add candidates via Admin panel. Cognito user created, temp password generated and issued.</div>
        </div>
        <div className="flow-stage">
          <div className="flow-stage-num">3</div>
          <div className="flow-stage-title">Configure Assessments</div>
          <div className="flow-stage-desc">Set up question scenarios, assign access, configure evaluation criteria.</div>
        </div>
        <div className="flow-stage">
          <div className="flow-stage-num">4</div>
          <div className="flow-stage-title">Monitor & Manage</div>
          <div className="flow-stage-desc">View active sessions, manage candidate status, deactivate/reset access as needed.</div>
        </div>
        <div className="flow-stage">
          <div className="flow-stage-num">5</div>
          <div className="flow-stage-title">Review Results</div>
          <div className="flow-stage-desc">Access AI-generated evaluation scores and detailed per-scenario feedback in the Evaluation panel.</div>
        </div>
      </div>

      {/* Candidate Flow */}
      <h3>Candidate Journey</h3>
      <div className="flow-visual flow-visual--horizontal">
        <div className="flow-stage">
          <div className="flow-stage-num">1</div>
          <div className="flow-stage-title">Login</div>
          <div className="flow-stage-desc">Candidate enters email + password. Redis cache checked first — HIT skips Cognito. MISS authenticates via Cognito.</div>
        </div>
        <div className="flow-stage">
          <div className="flow-stage-num">2</div>
          <div className="flow-stage-title">Assessment Portal</div>
          <div className="flow-stage-desc">Dashboard shows assigned assessments. Candidate clicks "Start Assessment" to begin session.</div>
        </div>
        <div className="flow-stage">
          <div className="flow-stage-num">3</div>
          <div className="flow-stage-title">AI Chat Sessions</div>
          <div className="flow-stage-desc">3 parallel chat windows with AI-generated customer personas. WebSocket delivers real-time messages (~4s LLM response).</div>
        </div>
        <div className="flow-stage">
          <div className="flow-stage-num">4</div>
          <div className="flow-stage-title">Round-robin</div>
          <div className="flow-stage-desc">Scenarios delivered sequentially — each evaluates different communication competencies. Disconnect recovery built-in.</div>
        </div>
        <div className="flow-stage">
          <div className="flow-stage-num">5</div>
          <div className="flow-stage-title">AI Evaluation</div>
          <div className="flow-stage-desc">On session complete, Prompt Orchestrator evaluates all conversations against scoring rubric. Results stored in DocumentDB.</div>
        </div>
      </div>
    </div>
  );
}

/* ─── Impact Tab ─── */
function ImpactTab() {
  return (
    <div className="tab-section">
      <h2 className="page-title">Impact</h2>
      <p className="section-desc">
        How CommsCoach supports the team and transforms the assessment process for Consumer Operations.
      </p>

      <div className="comparison-grid">
        <div className="comparison-card comparison-before">
          <h4>Before (Traditional)</h4>
          <table className="about-table">
            <tbody>
              <tr><td>Cycle Time</td><td><strong>5 hours</strong></td></tr>
              <tr><td>Per Interview</td><td>30 mins</td></tr>
              <tr><td>Chat Windows</td><td>1</td></tr>
              <tr><td>Evaluation</td><td>1 conversation/candidate</td></tr>
              <tr><td>Scenarios</td><td>Repeated over time</td></tr>
              <tr><td>Assessor Availability</td><td>Engaged all day</td></tr>
            </tbody>
          </table>
        </div>
        <div className="comparison-card comparison-after">
          <h4>After (With CommsCoach)</h4>
          <table className="about-table">
            <tbody>
              <tr><td>Cycle Time</td><td><strong>30 mins</strong></td></tr>
              <tr><td>Per Interview</td><td>15–20 mins</td></tr>
              <tr><td>Chat Windows</td><td>3 (concurrent)</td></tr>
              <tr><td>Evaluation</td><td>3 conversations/candidate</td></tr>
              <tr><td>Scenarios</td><td>Unique every time</td></tr>
              <tr><td>Assessor Availability</td><td>Free for high-value work</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* How CommsCoach Helps */}
      <h3>What This Means for the Team</h3>
      <div className="principles-grid">
        <div className="principle-card">
          <div className="principle-icon">⏰</div>
          <h4>Time Back to You</h4>
          <p>Automated assessment and scoring means assessors spend less time on repetitive tasks and more time on strategic decisions — reviewing results, mentoring, and improving processes.</p>
        </div>
        <div className="principle-card">
          <div className="principle-icon">📈</div>
          <h4>Scale Without Pressure</h4>
          <p>Handle peak hiring campaigns without burning out the team. CommsCoach runs assessments in parallel — 10 candidates or 100, the effort stays the same for you.</p>
        </div>
        <div className="principle-card">
          <div className="principle-icon">🎯</div>
          <h4>Better Hiring Decisions</h4>
          <p>Candidates are tested in realistic multi-chat environments. You see how they actually perform under real conditions — not just how well they prepare for exams.</p>
        </div>
        <div className="principle-card">
          <div className="principle-icon">🤝</div>
          <h4>Consistent & Fair</h4>
          <p>Every candidate gets the same standard of evaluation across 6 parameters. No variation between assessors or assessment days — fair for candidates, reliable for the business.</p>
        </div>
        <div className="principle-card">
          <div className="principle-icon">🎓</div>
          <h4>Training Made Easy</h4>
          <p>The same platform doubles as a training tool. New hires practise with realistic customer scenarios on their own — coaches step in for guidance rather than running every session manually.</p>
        </div>
        <div className="principle-card">
          <div className="principle-icon">🔒</div>
          <h4>Built-in Integrity</h4>
          <p>Copy-paste disabled, session time limits, and disconnect recovery — all handled automatically. No need to manually monitor candidate behaviour during assessments.</p>
        </div>
      </div>
    </div>
  );
}
